import { ColGroupDef } from 'ag-grid-community';
import { EXCEL_DATE_STYLE_ID } from 'src/constants/agGrid';
import { getYYYYMMDDUTCStrFromTimestamp } from 'src/util/grid/cellGetter';
import { dateFilterOptions } from 'src/util/grid/filter';
import { dateComparator, dateFilterComparator } from 'src/util/grid/sort';

export const reportingGridColDefs: ColGroupDef[] = [
    {
        headerName: 'Deal',
        children: [
            // Deal columns first
            {
                field: 'contractName',
                headerName: 'Contract',
                filter: 'agSetColumnFilter',
                minWidth: 150,
                sortable: true,
                resizable: true
            },
            {
                field: 'dealStatus',
                headerName: 'Status',
                filter: 'agSetColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'dealStartDate',
                headerName: 'Start Date',
                comparator: dateComparator,
                filter: 'agDateColumnFilter',
                filterParams: {
                    filterOptions: dateFilterOptions,
                    defaultOption: 'greaterThanOrEqual',
                    comparator: dateFilterComparator
                },
                cellClass: EXCEL_DATE_STYLE_ID,
                valueGetter: getYYYYMMDDUTCStrFromTimestamp,
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'dealEndDate',
                headerName: 'End Date',
                comparator: dateComparator,
                filter: 'agDateColumnFilter',
                filterParams: {
                    filterOptions: dateFilterOptions,
                    defaultOption: 'lessThanOrEqual',
                    comparator: dateFilterComparator
                },
                cellClass: EXCEL_DATE_STYLE_ID,
                valueGetter: getYYYYMMDDUTCStrFromTimestamp,
                minWidth: 120,
                sortable: true,
                resizable: true
            }
        ]
    },
    {
        headerName: 'Vendor',
        children: [
            // Entity/Vendor columns
            {
                field: 'entityName',
                headerName: 'Vendor',
                filter: 'agSetColumnFilter',
                minWidth: 150,
                sortable: true,
                resizable: true
            },
            {
                field: 'serviceName',
                headerName: 'Service',
                filter: 'agSetColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'territoryName',
                headerName: 'Territory',
                filter: 'agSetColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },

            // Vendor code columns
            {
                field: 'catalogCode',
                headerName: 'Catalog Code',
                filter: 'agSetColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'royaltyCode',
                headerName: 'Royalty Code',
                filter: 'agSetColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            }
        ]
    },

    {
        headerName: 'Rate',
        children: [
            // Core rates columns
            {
                field: 'adRevenueSharePercentage',
                headerName: 'Ad Revenue Share Percentage',
                filter: 'agNumberColumnFilter',
                minWidth: 200,
                sortable: true,
                resizable: true
            },
            {
                field: 'rateCardCurrency',
                headerName: 'Rate Card Currency',
                filter: 'agSetColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'settlementCurrency',
                headerName: 'Settlement Currency',
                filter: 'agSetColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'cae',
                headerName: 'CAE',
                filter: 'agSetColumnFilter',
                minWidth: 100,
                sortable: true,
                resizable: true
            },
            {
                field: 'playType',
                headerName: 'Play Type',
                filter: 'agSetColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'playlistTypeDefinition',
                headerName: 'Playlist Type Definition',
                filter: 'agSetColumnFilter',
                minWidth: 450,
                sortable: true,
                resizable: true
            },
            {
                field: 'customerType',
                headerName: 'Customer Type',
                filter: 'agSetColumnFilter',
                minWidth: 150,
                sortable: true,
                resizable: true
            },
            {
                field: 'programType',
                headerName: 'Program Type',
                filter: 'agSetColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'capPrsDeductions',
                headerName: 'Cap PRS Deductions (Percentage of Royalties)',
                filter: 'agSetColumnFilter',
                minWidth: 150,
                sortable: true,
                resizable: true
            },
            {
                field: 'royaltyModelType',
                headerName: 'Royalty Model Type',
                filter: 'agSetColumnFilter',
                minWidth: 150,
                sortable: true,
                resizable: true
            },
            {
                field: 'rate',
                headerName: 'Per Play Rate',
                filter: 'agNumberColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'ratchetModelName',
                headerName: 'Conversion Model',
                filter: 'agSetColumnFilter',
                minWidth: 150,
                sortable: true,
                resizable: true
            },
            {
                field: 'threshold1',
                headerName: 'Threshold 1 Rate',
                filter: 'agNumberColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'threshold2',
                headerName: 'Threshold 2 Rate',
                filter: 'agNumberColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'threshold3',
                headerName: 'Threshold 3 Rate',
                filter: 'agNumberColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'threshold4',
                headerName: 'Threshold 4 Rate',
                filter: 'agNumberColumnFilter',
                minWidth: 120,
                sortable: true,
                resizable: true
            },
            {
                field: 'blended1',
                headerName: '< 15% Blended Rate',
                filter: 'agNumberColumnFilter',
                minWidth: 150,
                sortable: true,
                resizable: true
            },
            {
                field: 'blended2',
                headerName: '< 25% Blended Rate',
                filter: 'agNumberColumnFilter',
                minWidth: 150,
                sortable: true,
                resizable: true
            },
            {
                field: 'blended3',
                headerName: '< 35% Blended Rate',
                filter: 'agNumberColumnFilter',
                minWidth: 150,
                sortable: true,
                resizable: true
            },
            {
                field: 'blended4',
                headerName: '< 100% Blended Rate',
                filter: 'agNumberColumnFilter',
                minWidth: 150,
                sortable: true,
                resizable: true
            }
        ]
    }
];
